// Cadastrar/Editar dispositivo → "Puxar dados da Agenda" (pedido 2026-10-14):
// veículo digitado à mão num agendamento (cliente ainda sem veículo
// cadastrado) preenche o cadastro — nome "MODELO - PLACA", marca, modelo,
// ano, placa e cor. Nunca mexe em IMEI/chip/rastreador. Se o cliente do
// agendamento já tem usuário, o veículo é vinculado a ele ao salvar.
// Mesmo visual do SalesVehiclePicker. Só equipe (o backend recusa cliente).
import { useEffect, useState } from "react";
import { format } from "date-fns";
import { CalendarDays, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/auth-context";
import { useAgendaManualVehicles } from "@/hooks/queries/agenda";
import { isRestrictedAccountType } from "@/lib/account-type";
import type { AgendaManualVehicle } from "@/services/gpsdrive-agenda.service";

const plateKey = (s: string) => s.replace(/[^a-z0-9]/gi, "").toUpperCase();

export function AgendaVehiclePicker({
  onPick,
  currentPlate,
}: {
  onPick: (pick: AgendaManualVehicle | null) => void;
  /** Editar dispositivo: agendamento com a mesma placa vem primeiro. */
  currentPlate?: string;
}) {
  const { gpsDriveUser } = useAuth();
  const allowed = !!gpsDriveUser && !isRestrictedAccountType(gpsDriveUser.accountType);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<AgendaManualVehicle | null>(null);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);
  // Sempre busca uma vez (sem texto) pra saber se há algo — o quadro só
  // aparece quando existe algum agendamento com veículo digitado.
  const { data, isLoading, isError } = useAgendaManualVehicles(debounced, allowed);

  if (!allowed || isError || (!debounced && !picked && (data?.length ?? 0) === 0)) return null;

  const mine = currentPlate ? plateKey(currentPlate) : "";
  const options = [...(data ?? [])].sort(
    (a, b) => Number(plateKey(b.vehicle.plate) === mine && !!mine) - Number(plateKey(a.vehicle.plate) === mine && !!mine),
  );

  const choose = (pick: AgendaManualVehicle | null) => {
    setPicked(pick);
    setOpen(false);
    setQuery("");
    onPick(pick);
  };

  const describe = (v: AgendaManualVehicle["vehicle"]) =>
    [[v.brand, v.model].filter(Boolean).join(" "), v.plate, v.year, v.color].filter(Boolean).join(" · ");

  return (
    <div className="mb-6 rounded-lg border border-dashed border-violet-500/40 bg-violet-500/5 p-4">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
        <CalendarDays className="h-4 w-4 text-violet-600" />
        Puxar dados da Agenda
      </div>
      {picked ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2 text-sm">
          <div className="min-w-0">
            <div className="truncate font-medium">{picked.clientName}</div>
            <div className="truncate text-xs text-muted-foreground">{describe(picked.vehicle)}</div>
          </div>
          <button
            type="button"
            onClick={() => choose(null)}
            className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            title="Trocar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              placeholder="Buscar cliente ou placa digitada no agendamento"
              className="pl-8"
            />
          </div>
          {open && (
            <div className="mt-2 max-h-64 overflow-y-auto rounded-md border border-border bg-background">
              {isLoading ? (
                <p className="px-3 py-2 text-xs text-muted-foreground">Carregando…</p>
              ) : options.length === 0 ? (
                <p className="px-3 py-2 text-xs text-muted-foreground">Nenhum veículo digitado na Agenda com essa busca.</p>
              ) : (
                options.map((o) => (
                  <button
                    key={o.appointmentId}
                    type="button"
                    onClick={() => choose(o)}
                    className="flex w-full items-center justify-between gap-3 border-b border-border px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium">{o.clientName}</div>
                      <div className="truncate text-xs text-muted-foreground">{describe(o.vehicle)}</div>
                    </div>
                    <span className="shrink-0 text-right text-[11px] text-muted-foreground">
                      {mine && plateKey(o.vehicle.plate) === mine ? (
                        <span className="font-semibold text-violet-600">Mesma placa</span>
                      ) : (
                        <>
                          {o.topicName}
                          <br />
                          {format(new Date(o.start), "dd/MM/yyyy")}
                        </>
                      )}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}
      <p className="mt-2 text-[11px] text-muted-foreground">
        Preenche nome, marca, modelo, ano, placa e cor (IMEI e chip nunca mudam). Se o cliente do agendamento já tem
        usuário, o veículo é vinculado a ele ao salvar.
      </p>
    </div>
  );
}
